import type {
  NodeKey,
  SlotInputNode,
  SlotValuationInputsResponse,
  TearsheetDate,
} from 'types/packages-api/slot-valuation-inputs';
import { controlForNode, nodeKeyForMilestone } from './editability';
import { EditAffordance, OverrideMarker } from './tearsheet-cells';
import { rowCanCarryOverride } from './override-badge';
import { type MetaByNode } from './tearsheet-sections';
import {
  dateSourceLabel,
  formatNodeValue,
  MILESTONE_LABELS,
  MILESTONE_ORDER,
  scheduleOperands,
  showsScheduleFormula,
  substitutedMilestoneFormula,
  type OperationField,
} from './utils';
import styles from './slot-tearsheet.module.scss';

/**
 * The four schedule milestones with their per-date provenance.
 *
 * ── AC 10 IS A PER-DATE RULE AND CANNOT BE EXPRESSED PER ZONE ────────────────────────
 *
 * The formula must be omitted where the date came from the file rather than the scheduler.
 * EC 60's DUC slot 4249535088 has permit and spud `onFile` while completion and first
 * production are `scheduled` — ALL FOUR in the development_pace zone. So a zone-level
 * predicate looks right on the PDP slots (all four onFile) and the LTD (all four scheduled)
 * and is wrong on the DUC. That is why formulas moved to per-row and the dates get their own
 * rows here.
 *
 * The formula shown for a scheduled date is the slot category's own schedule formula, read
 * from the registry rather than restated (see scheduleFormulaFor). The date NODES carry no
 * formula of their own — verified against a live payload — so there is nothing to suppress on
 * them; the modelled formula lives on the schedule function for the slot's category, and
 * that is the one that must not appear beside a reported date.
 *
 * ── `slotCategory` IS GONE FROM THIS COMPONENT (UAT 1) ───────────────────────────────────
 *
 * It existed to find the schedule function node for the slot's category and read its
 * formula. That resolution moved to the backend, which keys
 * ModelMap::SCHEDULE_MILESTONE_FORMULAS by category and serves each date its own line — so
 * the category is no longer an input to anything here. Removed rather than left unused: a
 * prop threaded in for a lookup that no longer happens is the next reader's wrong turn.
 * (`slotCategory` is still very much alive elsewhere on the page, for AC 4's rate emphasis.)
 *
 * @param {object} props - The payload's `dates`, the nodes and the served definitions.
 * @return {(JSX.Element|null)} The dates table, or null when the payload carries none.
 */
export function DatesTable({
  dates,
  nodes,
  meta,
  onRerun,
  operations,
  onRevert,
  editableKeys,
  onEdit,
}: {
  dates: SlotValuationInputsResponse['dates'] | undefined;
  nodes: SlotInputNode[];
  meta: MetaByNode;
  onRerun?: () => void;
  operations?: Map<string, OperationField[]>;
  onRevert?: (node: SlotInputNode) => void;
  /** The development_pace section's own set, THREADED rather than recomputed — the edit rule
   *  lives in editability.isNodeEditable and nowhere else. */
  editableKeys?: Set<NodeKey>;
  onEdit?: (node: SlotInputNode, rowLabel: string) => void;
}) {
  // Absent on a response from an older build — render nothing rather than an
  // empty table claiming the slot has no dates.
  if (!dates) {
    return null;
  }
  // ── ONE FORMULA PER ROW, SELECTED NOT PARSED (UAT 1) ──────────────────────────────
  // `scheduleFormulaFor` returned the schedule node's whole multi-line formula and every
  // row printed it. Each date now carries its own `formula` and its own `formulaInputs`,
  // declared in ModelMap::SCHEDULE_MILESTONE_FORMULAS and served per milestone. Nothing
  // here splits a string — which was the whole reason for choosing the registry.

  return (
    <table className={styles.paramTable}>
      <thead>
        <tr>
          <th className={styles.paramNameHead}>Milestone</th>
          <th className={styles.paramValueHead}>Date</th>
        </tr>
      </thead>
      <tbody>
        {MILESTONE_ORDER.map((milestone) => {
          const merged = dates[milestone];
          // THIS row's line, not the block's. Absent on a pdp (the registry declares none)
          // and on a payload written before UAT 1 — both read as "no formula", which is the
          // behaviour those rows already had.
          const rowFormula = merged?.formula ?? null;
          const showFormula =
            showsScheduleFormula(merged?.source) && Boolean(rowFormula);
          // UAT 2b: the variable-name line and the operand-value line collapse
          // into one, with the values in place — `09/01/2026 + 7 months = 04/01/2027`. null
          // for a line that is not `base + N months` (the two irregular LTD rows) and until
          // the model map lands; those keep the fallback below.
          const substituted = showFormula
            ? substitutedMilestoneFormula(milestone, dates, nodes, meta)
            : null;
          // Only the fallback needs the operands broken out; the one-line form carries them.
          const operands =
            showFormula && !substituted
              ? scheduleOperands(merged?.formulaInputs, nodes, meta)
              : [];

          return (
            <tr key={milestone}>
              <td className={styles.paramName}>
                {MILESTONE_LABELS[milestone]}
                {/* ── ONE LINE, VALUES SUBSTITUTED (UAT 2b) ────────────────────────
                    Collapse the variable-name formula and its operand-value line
                    into a single `<date> + <N> months = <date>`. A resolved 0-month lag
                    renders as "0" — a zero lag is real and ordinary — while an unresolved
                    lag (or base) renders faded/absent, never as 0. The parts are read from
                    the payload, not computed; see substitutedMilestoneFormula. */}
                {substituted ? (
                  <span className={styles.formula}>
                    <span
                      className={
                        substituted.base.resolved
                          ? undefined
                          : styles.nodeValueFaded
                      }
                    >
                      {substituted.base.text}
                    </span>
                    {' + '}
                    <span
                      className={
                        substituted.lag.resolved
                          ? undefined
                          : styles.nodeValueFaded
                      }
                    >
                      {substituted.lag.text}
                    </span>
                    {' months = '}
                    {substituted.result}
                  </span>
                ) : (
                  showFormula && (
                    <>
                      {/* FALLBACK: a line we cannot express as base + N months (the two
                          irregular LTD rows), or a payload whose model map has not loaded.
                          The variable-name formula and its operand values, as UAT 2 showed
                          them — an unresolved operand faded, a real 0 shown as 0. */}
                      <span className={styles.formula}>{rowFormula}</span>
                      {operands.length > 0 && (
                        <span className={styles.operands}>
                          {operands.map((operand, index) => (
                            <span key={operand.key}>
                              {index > 0 && ' · '}
                              {operand.label}
                              {' = '}
                              <span
                                className={
                                  operand.resolved
                                    ? undefined
                                    : styles.nodeValueFaded
                                }
                              >
                                {operand.text}
                              </span>
                            </span>
                          ))}
                        </span>
                      )}
                    </>
                  )
                )}
              </td>
              <td className={styles.paramValue}>
                {/* ── THE NODE ROWS' OWN ALIGNMENT PRIMITIVE, NOT A SECOND ONE ─────────
                    These three rows laid the value, the badge and Edit out inline, so Edit
                    sat wherever the text ended — while `First Production Date (in the
                    cashflow)`, one table down, put its Edit on the row's right edge. One
                    affordance, two alignments, on one screen.
                    `.valueCellRow` + `.valueCellMain` is the pair the Parameter/Input rows
                    already use (see the node row below), so this is the same rule applied
                    here rather than a milestone-specific copy of it. It also brings the two
                    behaviours those styles already own: `min-width: 0` on the main cell
                    keeps a long value wrapping instead of pushing the control out, and
                    `> * { flex: 0 0 auto }` holds for the LOCKED shape too, whose flex
                    child is Carbon's Toggletip wrapper rather than an element of ours.
                    The badge stays INSIDE the main cell, beside the value it qualifies —
                    again matching the node rows, where the override marker (Revert
                    included) sits in `.valueCellMain` and only Edit is the right-aligned
                    sibling. */}
                <div className={styles.valueCellRow}>
                  <div className={styles.valueCellMain}>
                    {formatNodeValue(merged?.value ?? null, 'date')}
                    {/* ── THE ONE PROVENANCE CLAIM THAT SURVIVES THE COLUMN'S REMOVAL ──────
                    The Source column is gone. `onFile` and `scheduled`
                    lose nothing by it: they are the ordinary cases, and the schedule
                    formula beside the milestone name already marks a scheduled date.
                    `derivedFromFirstProduction` is not ordinary — the blob carries no
                    completion date and this value is first production standing in for it,
                    on 30 of the 45 valued slots measured. Without a mark, a derived date
                    renders identically to a read one and yesterday's fix is undone by a
                    different route.
                    ON THE VALUE, VISIBLY, NOT ON HOVER. A title attribute is not a
                    provenance claim anyone can cite from a screenshot. */}
                    {merged?.source === 'derivedFromFirstProduction' && (
                      <span
                        className={styles.derivedMarker}
                        title={dateSourceLabel(merged.source)}
                      >
                        derived
                      </span>
                    )}
                    <MilestoneOverrideMarker
                      milestone={milestone}
                      merged={merged}
                      nodes={nodes}
                      meta={meta}
                      onRerun={onRerun}
                      operations={operations}
                      onRevert={onRevert}
                    />
                  </div>
                  <MilestoneEditAffordance
                    milestone={milestone}
                    merged={merged}
                    nodes={nodes}
                    meta={meta}
                    editableKeys={editableKeys}
                    onEdit={onEdit}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── THE SAME MARKER, ON THE MILESTONES TABLE ────────────────────────────────────────────
//
// Permit, spud, completion and first production render TWICE on this page: once in their
// development_pace zone row and once here, and this is the table a reader actually looks at
// for dates. Marking only the zone row would leave the prominent rendering of four of the
// eleven overridable fields silent about an override that is real.
//
// The node is found through nodeKeyForMilestone, which inverts the ONE declared milestone
// map rather than adding a second — `completion_date`'s milestone is `modeledCompletionDate`
// and a re-derived map gets that wrong silently.
// ── THE EDIT SITS ON THE ROW THAT SHOWS THE RESULT ──────────────────────────────────────
//
// It used to sit on "Completion Date (scheduled)", a row whose blob the scheduler rewrites
// from the pace scenario every run without reading an override — so the button changed a
// number that appeared two tables away, on "Completion Date (in use)". From UAT:
// "we have the edit buttons next to the scheduled and the override gets shown on the In
// use… not very user friendly."
//
// NOTHING ABOUT THE WRITE CHANGES. The same node resolved through the same
// nodeKeyForMilestone inversion is handed to the same `onEdit`, so the same editor opens and
// the same override key (`completion_date` and friends) is written on the same engine. This
// is where the button lives, not what it does.
//
// THE LOCK IS THE MERGER'S OWN. `merged.locked` is the field isNodeLocked reads through
// LOCK_MILESTONE_BY_NODE — one fact, read here at its source, so a reported milestone
// (permit on a Permit well, spud on a DUC, all four on a PDP) renders "locked" and no
// button exactly as the node rows do.
function MilestoneEditAffordance({
  milestone,
  merged,
  nodes,
  meta,
  editableKeys,
  onEdit,
}: {
  milestone: string;
  merged?: TearsheetDate;
  nodes: SlotInputNode[];
  meta: MetaByNode;
  editableKeys?: Set<NodeKey>;
  onEdit?: (node: SlotInputNode, rowLabel: string) => void;
}) {
  const nodeKey = nodeKeyForMilestone(milestone, meta);
  const node = nodeKey ? nodes.find((n) => n.key === nodeKey) : undefined;
  if (!node) {
    return null;
  }
  // ONLY THE ROWS WHOSE EDIT HAD NOWHERE HONEST TO LIVE. first_production_date reads the
  // cashflow parquet, which the run rewrites FROM the override, so its own node row shows
  // the result and keeps its button; adding a second one here would give that field two.
  if (rowCanCarryOverride(meta[node.key])) {
    return null;
  }
  return (
    <EditAffordance
      node={node}
      editable={Boolean(editableKeys?.has(node.key))}
      locked={Boolean(merged?.locked)}
      control={controlForNode(meta[node.key])}
      onEdit={onEdit}
      rowLabel={MILESTONE_LABELS[milestone as keyof typeof MILESTONE_LABELS]}
    />
  );
}

function MilestoneOverrideMarker({
  milestone,
  merged,
  nodes,
  meta,
  onRerun,
  operations,
  onRevert,
}: {
  milestone: string;
  /** The row's own merged date. Handed down so the badge describes what this row renders
   *  rather than what the registry node holds — see OverrideMarker's `merged`. */
  merged?: TearsheetDate;
  nodes: SlotInputNode[];
  meta: MetaByNode;
  onRerun?: () => void;
  operations?: Map<string, OperationField[]>;
  onRevert?: (node: SlotInputNode) => void;
}) {
  const nodeKey = nodeKeyForMilestone(milestone, meta);
  const node = nodeKey ? nodes.find((n) => n.key === nodeKey) : undefined;
  if (!node) {
    return null;
  }
  return (
    <OverrideMarker
      node={node}
      unit={meta[node.key]?.unit}
      // The MILESTONE's name, not the node's — this row is "Spud Date", the node is
      // `spud_date (scheduled)`. Same reason EditAffordance takes one.
      rowLabel={MILESTONE_LABELS[milestone as keyof typeof MILESTONE_LABELS]}
      merged={merged}
      // PASSED, THOUGH `merged` is what exempts this row from the scheduled-blob gate. Not
      // passing it would make the exemption depend on a prop being absent — the kind of
      // implicit coupling that survives a refactor and takes the badge with it.
      meta={meta[node.key]}
      onRerun={onRerun}
      operations={operations}
      onRevert={onRevert}
    />
  );
}
